
import { Component, Mixins, Watch } from 'vue-property-decorator'

import {
  WPList,
  WPCard,
  WPSelect,
  WPButton,
  WPDivider,
  WPTitle,
  WPCombobox,
  WPTextfield,
  WPOpacity,
} from '@/components'
import {
  ClustersSearchMixin,
  FieldsSearchMixin,
  WellTypesSearchMixin,
} from '@/mixins'

import { Field } from '@/store/field'
import { Dictionary } from 'vue-router/types/router'
import { ICluster, IWell, IWellTypes } from '@/types'

@Component({
  components: {
    WPList,
    WPCard,
    WPSelect,
    WPButton,
    WPDivider,
    WPTitle,
    WPCombobox,
    WPTextfield,
    WPOpacity,
  },
})
export default class Wells extends Mixins(
  FieldsSearchMixin,
  ClustersSearchMixin,
  WellTypesSearchMixin
) {
  private filterField: number | null = null

  private filterType: number | null = null

  private filterCluster: number | null = null

  private isFiltersOpened = false

  private busy = false

  private search = ''

  private selectedWellType: string | number | null | IWellTypes = null

  private wellName = ''

  private selectedWellId = 0

  private selectedCluster: number | ICluster | null = null

  private selectedFieldId = 0

  private isAddDialog = false

  private isEdit = false

  private isShownWellList = false

  private loading = false

  private get title() {
    return this.$t('wells.emptyWells')
  }

  private get wells() {
    return Field.wellsFull
  }

  private get emptyCountWells() {
    return Field.counts.wellsFull === 0
  }

  private get query() {
    if (
      this.search ||
      this.filterField ||
      this.filterType ||
      this.filterCluster
    ) {
      let result: Dictionary<string> = {}
      if (this.search) result.name = this.search
      if (this.filterField) result.field = String(this.filterField)
      if (this.filterType) result.welltype = String(this.filterType)
      if (this.filterCluster) result.cluster = String(this.filterCluster)
      return result
    } else {
      return undefined
    }
  }

  private get fields() {
    return Field.fields
  }

  private get types() {
    return Field.welltypes
  }

  private get welltypes() {
    return Field.welltypes
  }

  private get clusters() {
    return Field.clusters
  }

  private goToFields() {
    void this.$router.push({
      name: 'Fields',
    })
  }

  private cancel() {
    this.isFiltersOpened = false
    this.isAddDialog = false
  }

  private toggleFilters() {
    this.isFiltersOpened = !this.isFiltersOpened
  }

  private async addWell() {
    this.loading = true
    if (this.selectedWellType) {
      let type = 0
      if (typeof this.selectedWellType === 'string') {
        const result = await Field.ADD_WELL_TYPE(this.selectedWellType)
        type = result ? result.id : 0
      } else if (typeof this.selectedWellType === 'number') {
        type = this.selectedWellType
      } else {
        type = this.selectedWellType.id
      }

      let cluster: number | null = null
      if (this.selectedCluster !== null) {
        if (
          typeof this.selectedCluster === 'string' &&
          this.selectedCluster === ''
        ) {
          cluster = null
        } else if (typeof this.selectedCluster === 'string') {
          const result = await Field.ADD_CLUSTER({
            name: this.selectedCluster,
            field: this.selectedFieldId,
          })
          cluster = result && result.id ? result.id : null
        } else if (typeof this.selectedCluster === 'number') {
          cluster = this.selectedCluster
        } else {
          cluster = this.selectedCluster.id || null
        }
      }

      if (this.isEdit) {
        const data: IWell = {
          id: this.selectedWellId,
          name: this.wellName,
          field: this.selectedFieldId,
          welltype: type,
          cluster,
        }
        await Field.SAVE_WELL_FULL(data)
      }
      await this.onLoad()
    }
    this.cancel()
    if (this.search) {
      void Field.CLEAR_DATA('wells')
      await this.onSearch(this.search, true)
    }
    this.loading = false
  }

  private async clearFilter() {
    this.filterField = null
    this.filterType = null
    this.filterCluster = null
    this.cancel()
    await this.confirmFilter()
  }

  private async confirmFilter() {
    void Field.CLEAR_DATA('wellsFull')
    await this.onLoad()
  }

  private getWellFieldId(id: string) {
    const item = this.wells.find((item) => item.id === Number(id))
    if (item) {
      const fieldId =
        typeof item.field === 'object'
          ? item.field && item.field?.id
          : item.field
      return {
        well: item,
        fieldId,
      }
    } else return null
  }

  private async onDeleteItem(id: number) {
    await Field.DELETE_WELL_FULL(id)
    if (this.search) {
      void Field.CLEAR_DATA('wellsFull')
      await this.onSearch(this.search, true)
    }
  }

  private async onEditItem(id: number) {
    const well = this.wells.find((item) => item.id === id)
    if (well) {
      const fieldId =
        typeof well.field === 'number' ? well.field : Number(well.field.id)
      await Field.GET_CLUSTERS({ field: fieldId })
      this.selectedWellType = well.welltype
      this.wellName = well.name
      this.selectedWellId = id
      this.selectedFieldId = fieldId
      if (well.cluster) this.selectedCluster = well.cluster
      this.isEdit = true
      this.isAddDialog = true
    }
  }

  private async onLoadWellTypes() {
    if (!this.busy) {
      this.busy = true
      await Field.GET_WELL_TYPES()
      this.busy = false
    }
  }

  private async onSearch(val: string, force?: boolean) {
    if (this.search !== val || force) {
      this.search = val
      await this.onLoad()
    }
  }

  private async onLoad() {
    if (!this.busy) {
      this.busy = true
      await Field.GET_WELLS_FULL({ query: this.query })
      this.busy = false
    }
  }

  private onClickItem(id: string) {
    const result = this.getWellFieldId(id)
    if (result) {
      const { fieldId, well } = result
      void Field.CLEAR_DATA('wellbores')
      void this.$router.push({
        name: 'TheWell',
        params: { id: String(fieldId), wellId: String(well.id) },
      })
    }
  }

  private onActionItem(id: string) {
    const result = this.getWellFieldId(id)
    if (result) {
      const { fieldId, well } = result
      void Field.CLEAR_DATA('wells')
      void this.$router.push({
        name: 'TheField',
        params: { id: String(fieldId) },
        query: { search: well.name },
      })
    }
  }

  @Watch('filterField')
  private async onChangeField(val: number) {
    void Field.CLEAR_DATA('clusters')
    this.filterCluster = null
    await Field.GET_CLUSTERS({ field: val })
  }

  public async onLoadFields() {
    await Field.GET_FIELDS(this.queryField)
  }

  public async onLoadTypes() {
    await Field.GET_WELL_TYPES(this.queryTypes)
  }

  public async onLoadClusters(search?: string) {
    await Field.GET_CLUSTERS({ field: Number(this.filterField), name: search })
  }

  private async mounted() {
    this.busy = true
    await Field.GET_WELLS_FULL({ query: this.query, clear: true })
    await Field.GET_WELL_TYPES()
    this.busy = false
    this.isShownWellList = true
  }

  private beforeDestroy() {
    if (
      this.search ||
      this.filterField ||
      this.filterType ||
      this.filterCluster
    )
      void Field.CLEAR_DATA('wellsFull')
  }
}
